import { Component, Optional } from '@angular/core';
import { environment } from '@env/environment';
import { Logger } from './core/logger.service';
import { GoogleAnalyticsService } from './google-analytics.service';
import { AuthUserActivityService } from './core/auth/auth-user-activity.service';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventType, InteractionStatus } from '@azure/msal-browser';
import { filter, Subject, takeUntil } from 'rxjs';
import { MyAccountService } from '@app/core/my-account.service';

const log = new Logger('App');

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    standalone: false
})
export class AppComponent {

  private readonly _destroying$ = new Subject<void>();

  isIframe: boolean = false;

  get isB2cEnabled(): boolean {
    return environment.b2c !== undefined;
  }

  constructor(
    private _gaService: GoogleAnalyticsService,
    private _myAccountService: MyAccountService,
    @Optional() private _authUserActivityService: AuthUserActivityService | null,
    @Optional() private _msalService: MsalService | null,
    @Optional() private _msalBroadcastService: MsalBroadcastService | null,
  ) { }

  ngOnInit() {
    console.log("Here is the base URL sourced from document", document.location.origin);

    // initialize google analytics
    this._gaService.init();

    // Setup logger
    if (environment.production) {
      Logger.enableProductionMode();
    }

    if (!this.isB2cEnabled) {
      // initialize user activity events
      this._authUserActivityService!.initListeners();
    } else {
      this._msalService!.handleRedirectObservable().subscribe();

      // This is to avoid reload during acquireTokenSilent() because of hidden iframe
      this.isIframe = window !== window.parent && !window.opener;

      this._msalService!.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
      this._msalBroadcastService!.msalSubject$
        .pipe(
          filter(msg => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
        )
        .subscribe(() => {
          if (this._msalService!.instance.getAllAccounts().length === 0) {
            window.location.pathname = '/';
          } else {
            this.refreshAuthenticatedState();
          }
        });

      this._msalBroadcastService!.inProgress$
        .pipe(
          filter(status => status === InteractionStatus.None),
          takeUntil(this._destroying$)
        )
        .subscribe(() => {
          this.refreshAuthenticatedState();
          this.checkAndSetActiveAccount();
        });

      this.refreshAuthenticatedState();
    }
  }

  refreshAuthenticatedState() {
    if (!this.isB2cEnabled) {
      throw new Error('MSAL configuration is missing: environment.b2c is undefined. Check your environment setup.');
    }
    if (this._myAccountService.isAuthenticated) {
      const activeAccount = this._msalService!.instance.getAllAccounts()[0];
      // this._b2cAuthService.getAuthorizedOrganizationsForMsalAccount(activeAccount).subscribe(orgs => {
      //   this._myAccountService.authorizedOrganizations = orgs;
      // })
    } else {
      // this._myAccountService.authorizedOrganizations = [];
    }
  }

  checkAndSetActiveAccount() {
    if (!this.isB2cEnabled) {
      throw new Error('MSAL configuration is missing: environment.b2c is undefined. Check your environment setup.');
    }
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    // TODO - maybe remove the check for active account and always pull first element as a better source of truth
    let activeAccount = this._msalService!.instance.getActiveAccount();
    if (!activeAccount && this._msalService!.instance.getAllAccounts().length > 0) {
      // Set the first account as active (common for single-user SPAs like B2C)
      let accounts = this._msalService!.instance.getAllAccounts();
      this._msalService!.instance.setActiveAccount(accounts[0]);
    }
  }
  
  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
